import { Button, Card, Heading, Icon, If, Pill, Progress, Stack, Text, View } from '@fold-dev/core'
import { useEffect, useState } from 'react'
import { TbFilters } from 'react-icons/tb'
import { useNavigate, useParams } from 'react-router'
import { Block } from '../components'
import { useDocumentTitle } from '../helpers'
import { useProjects } from '../hooks/api.hooks'
import { LogoNew } from '../logo'

const Disclaimer = () => {
    return (
        <Text
            textAlign="center"
            colorToken="indigo-50">
            By using Fronteer, you agree to our{' '}
            <a
                href="https://fronteer.app/terms-of-use"
                target="_blank"
                style={{ color: 'currentColor' }}>
                terms of use
            </a>{' '}
            and{' '}
            <a
                href="https://fronteer.app/license"
                target="_blank"
                style={{ color: 'currentColor' }}>
                license agreement
            </a>
            .
        </Text>
    )
}

export default (props) => {
    const projectsAPI = useProjects()
    const [error, setError] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [joined, setJoined] = useState(false)
    const [id, setId] = useState('')
    const [image, setImage] = useState('')
    const [name, setName] = useState('')
    const [icon, setIcon] = useState('')
    const [color, setColor] = useState('')
    const [isChannel, setIsChannel] = useState(false)
    const [workspace, setWorkspace] = useState('')
    const [workspaceId, setWorkspaceId] = useState('')
    const [team, setTeam] = useState('')
    const [teamId, setTeamId] = useState('')
    const [description, setDescription] = useState('')
    const { shortcode } = useParams()
    const navigate = useNavigate()

    const handleProjectJoin = async () => {
        setLoading(true)
        setError(false)

        try {
            await projectsAPI.join({ shortcode, id, workspaceId, teamId })

            setLoading(false)
            setJoined(true)
        } catch (e) {
            setLoading(false)
            setError('Could not join project, you might already be part of it.')
        }
    }

    useDocumentTitle(name)

    useEffect(() => {
        ;(async () => {
            try {
                setLoading(true)

                const result: any = await projectsAPI.shortcode({ shortcode })
                const { id, icon, image, name, workspace, description, color, team, isChannel } = result

                setLoading(false)
                setId(id)
                setImage(image)
                setIcon(icon)
                setColor(color)
                setIsChannel(isChannel)
                setWorkspace(workspace.name)
                setWorkspaceId(workspace.id)
                setTeam(team?.name)
                setTeamId(team?.id)
                setName(name)
                setDescription(description)
            } catch (e) {
                setLoading(false)
                setError('This project does not exist')
            }
        })()
    }, [])

    return (
        <View
            column
            gap="1rem"
            width="100%"
            height="100%"
            position="absolute">
            <If if={loading}>
                <Progress
                    value={50}
                    thickness={5}
                    indeterminate
                    bgToken="transparent"
                    position="absolute"
                    variant="success"
                    style={{
                        '--f-progress-radius': 0,
                        'top': 0,
                        'left': 0,
                    }}
                />
            </If>

            <View
                position="absolute"
                style={{ left: 30, bottom: 30 }}>
                <LogoNew
                    scale={0.25}
                    color="var(--f-color-text)"
                />
            </View>

            <Card
                column
                p="65px 50px"
                border="none"
                width={400}>
                <Stack
                    direction="vertical"
                    spacing={20}
                    justifyContent="center"
                    noStretch>
                    <Block
                        src={image}
                        name={name}
                        icon={icon}
                        isChannel={isChannel}
                        color={color}
                        className="mb-20"
                        size="2xl"
                    />

                    <Heading>{name}</Heading>

                    <View
                        row
                        className="project-owner">
                        <Pill
                            size="sm"
                            height={30}
                            p="0rem 0.25rem">
                            {workspace}
                        </Pill>
                        {!!team && (
                            <Pill
                                size="sm"
                                height={30}
                                prefix={<Icon icon={TbFilters} />}
                                p="0rem 0.25rem">
                                {team}
                            </Pill>
                        )}
                    </View>

                    <If if={!joined}>
                        <View textAlign="center">
                            <div
                                style={{ textAlign: 'center' }}
                                className="f-text"
                                dangerouslySetInnerHTML={{ __html: description }}
                            />
                        </View>

                        <Button
                            size="lg"
                            variant="accent"
                            onClick={handleProjectJoin}
                            disabled={loading}>
                            Join Now
                        </Button>
                    </If>

                    <If if={joined}>
                        <Text
                            textAlign="center"
                            size="lg">
                            Congratulations!
                        </Text>
                        <Text textAlign="center">
                            You have successfully joined this project. Click on the button to get started.
                        </Text>
                        <Button
                            size="lg"
                            onClick={() => navigate(`/app/workspace/${workspaceId}/c/${id}/list`)}
                            disabled={loading}>
                            Start
                        </Button>
                    </If>

                    <If if={error}>
                        <Text
                            size="sm"
                            fontWeight="bold"
                            onClick={() => setError(null)}
                            colorToken="danger"
                            textAlign="center">
                            {error}
                        </Text>
                    </If>
                </Stack>
            </Card>

            <View width={400}>
                <Disclaimer />
            </View>
        </View>
    )
}
